import dayjs from 'dayjs'
import utcPlugin from 'dayjs/plugin/utc'
import timezonePlugin from 'dayjs/plugin/timezone'
import {
  getTimezoneAbbreviation,
  resolveEffectiveTimezone,
} from './timezone-display'

// Existing helpers here call .utc()/.tz(); make that safe regardless of import order
dayjs.extend(utcPlugin)
dayjs.extend(timezonePlugin)

/**
 * Formats a game date into uppercase format
 * Example output: "JAN 1 - 11:59 AM"
 * @param gameDate - dayjs date object to format
 * @returns Formatted date string in uppercase
 */
export const formatGameDate = (gameDate: dayjs.Dayjs): string => {
  const formattedDate: string = gameDate.format('MMM D - h:mm A').toUpperCase()
  return formattedDate
}

/**
 * Formats a date string to display in a long format with ordinal day
 * Example output: "January 1, 2024 11:59 AM"
 * @param dateString - ISO date string to format
 * @returns Formatted date string
 */
export const formatLongDateTime = (dateString: string): string => {
  return dayjs(dateString).utc().format('MMMM D, YYYY h:mm A')
}

/**
 * Formats a date string to display in a short format with time
 * Example output: "Jan 1, 2024 11:59 AM"
 * @param dateString - ISO date string to format
 * @param useUtc - Whether to use UTC timezone (default: true)
 * @param timezone - Optional timezone to convert to (e.g. 'America/New_York')
 * @returns Formatted date string
 */
export const formatShortDateTime = (
  dateString: string,
  useUtc: boolean = true,
  timezone?: string
): string => {
  let date = useUtc ? dayjs(dateString).utc() : dayjs(dateString)
  if (timezone) {
    date = date.tz(timezone)
  }
  return date.format('MMM D, YYYY h:mm A')
}

/**
 * Formats a date string to display in a long format with ordinal day
 * Example output: "January 1, 2024"
 * @param dateString - ISO date string to format
 * @returns Formatted date string
 */
export const formatLongDate = (dateString: string): string => {
  return dayjs(dateString).utc().format('MMMM D, YYYY')
}

/**
 * Formats a date string to display in a short format
 * Example output: "Jan 1, 2024"
 * @param dateString - ISO date string to format
 * @returns Formatted date string
 */
export const formatShortDate = (
  dateString: string,
  useUtc: boolean = true
): string => {
  const date = useUtc ? dayjs(dateString).utc() : dayjs(dateString)
  return date.format('MMM D, YYYY')
}

/**
 * Formats a stored UTC timestamp as date + time + timezone abbreviation.
 * Example output: "Jul 16, 2026 4:25 PM PDT"
 *
 * Rendered in `timezone` when given, otherwise the viewer's own zone — never
 * UTC, which would show a time nobody recognizes. The abbreviation is resolved
 * at that instant, so it correctly reads PDT in summer and PST in winter.
 *
 * @param dateString - ISO date string (UTC) to format
 * @param timezone - optional IANA timezone (e.g. 'America/New_York')
 * @returns formatted string, or '' for empty/unparseable input
 */
export const formatDateTimeWithZone = (
  dateString: string | null | undefined,
  timezone?: string | null
): string => {
  if (!dateString) {
    return ''
  }

  const asUtc = dayjs.utc(dateString)
  if (!asUtc.isValid()) {
    return ''
  }

  const effectiveTimezone = resolveEffectiveTimezone(timezone)
  const zoned = effectiveTimezone ? asUtc.tz(effectiveTimezone) : asUtc
  const abbreviation = effectiveTimezone
    ? getTimezoneAbbreviation(effectiveTimezone, asUtc.toDate())
    : 'UTC'

  const formatted = zoned.format('MMM D, YYYY h:mm A')
  return abbreviation ? `${formatted} ${abbreviation}` : formatted
}

/**
 * Returns a date string in ISO format with the time set to 23:58:58 UTC,
 * This is used to indicate that the game time is to be defined.
 * @param isoDateString - ISO date string (e.g., from locationStartDate.toUTC().toISO())
 * @returns ISO date string with time set to 23:58:58
 */
export const setTimeToBeDefined = (isoDateString: string): string => {
  if (typeof isoDateString !== 'string' || isoDateString.length === 0) {
    return isoDateString
  }
  return dayjs(isoDateString)
    .utc()
    .set('hour', 23)
    .set('minute', 58)
    .set('second', 58)
    .set('millisecond', 0)
    .toISOString()
}

/**
 * Returns a date string in ISO format with the time set to 23:58:58 UTC,
 * This is used to indicate that the game time is to be defined.
 * @param isoDateString - ISO date string (e.g., from locationStartDate.toUTC().toISO())
 * @returns ISO date string with time set to 23:58:58
 */
export const setTimeToBeDefinedNoISO = (isoDateString: string): string => {
  if (typeof isoDateString !== 'string' || isoDateString.length === 0) {
    return isoDateString
  }
  return dayjs(isoDateString)
    .utc()
    .set('hour', 23)
    .set('minute', 58)
    .set('second', 58)
    .set('millisecond', 0)
    .toString()
}

/**
 * Clears the "to be defined" sentinel time from a date, keeping the calendar
 * date but setting the time-of-day to the current time. Used when a user
 * toggles TBD off, so the picker pre-selects "now" instead of 23:58:58.
 * @param isoDateString - ISO date string holding the TBD sentinel time
 * @param timezone - IANA timezone the date should be interpreted in (e.g. the
 * venue's). Falls back to local time when omitted.
 * @returns ISO date string on the same date with the current time-of-day
 */
export const clearTimeToBeDefined = (
  isoDateString: string,
  timezone?: string | null
): string => {
  if (typeof isoDateString !== 'string' || isoDateString.length === 0) {
    return isoDateString
  }

  const zoned = timezone
    ? dayjs(isoDateString).tz(timezone)
    : dayjs(isoDateString)
  const now = timezone ? dayjs().tz(timezone) : dayjs()

  return zoned
    .set('hour', now.hour())
    .set('minute', now.minute())
    .set('second', now.second())
    .set('millisecond', 0)
    .toISOString()
}

/**
 * Checks if the given ISO date string has the time set to 23:58:58 UTC,
 * which indicates that the game time is to be defined.
 * @param isoDateString - ISO date string to check
 * @returns True if the time is set to 23:58:58 UTC, false otherwise
 */
export const isTimeToBeDefined = (isoDateString: string): boolean => {
  if (typeof isoDateString !== 'string' || isoDateString.length === 0) {
    return false
  }
  const date = dayjs(isoDateString).utc()
  return (
    date.hour() === 23 &&
    date.minute() === 58 &&
    date.second() === 58 &&
    date.millisecond() === 0
  )
}
